import { Component, computed, OnDestroy, OnInit, signal, WritableSignal, inject } from '@angular/core';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterModule } from '@angular/router';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../../store/app/app.selectors';
import { filter, Observable, Subscription, take } from 'rxjs';
import { CollapseSideNav, ToggleSideNav } from '../../../store/app/app.actions';
import { NgbModal, NgbCollapse } from '@ng-bootstrap/ng-bootstrap';
import { SetUpTutorialModalComponent } from '../modals/set-up-tutorial-modal/set-up-tutorial-modal.component';
import { SubscriptionSelectors } from '../../../store/subscription/subscription.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavItem } from '../../interfaces/project-interfaces';
import { ResponsiveService } from '../../../_services/responsive-service/responsive.service';
import { helpCenterUrl } from '../../../_fixtures/project-fixtures';

@Component({
  selector: 'app-sidenav',
  imports: [NgOptimizedImage, RouterLink, RouterModule, NgbCollapse, NgClass],
  templateUrl: './sidenav.component.html',
})
export class SidenavComponent implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly modalService = inject(NgbModal);
  readonly router = inject(Router);
  readonly responsiveService = inject(ResponsiveService);

  readonly userProfile$ = this.store.select(AppSelectors.userDetails);
  readonly hasNavigationAccess$ = this.store.select(AppSelectors.hasNavigationAccess);
  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);
  readonly hasNavigationAccess = toSignal(this.hasNavigationAccess$, { initialValue: false });

  pageTitle!: Observable<string>;
  sideNavOpen$: Observable<boolean>;
  sideNavOpen: boolean = false;
  helpCollapsed: boolean = true;
  isLoading: boolean = true;
  readonly subscriptions = new Subscription();
  readonly helpCenterUrl = helpCenterUrl;

  public collapseState: Record<string, boolean> = {};

  public _baseNavItems: NavItem[] = [
    {
      id: 'nav_dashboard',
      label: 'Dashboard',
      iconClass: 'pays-icon ic-home',
      routerLink: 'dashboard',
    },
    {
      id: 'nav_employees',
      label: 'Employees',
      iconClass: 'iconoir-group',
      children: [
        { label: 'Active employees', routerLink: 'employees' },
        { label: 'Invited employees', routerLink: 'employee-invitations' },
      ],
    },
    {
      id: 'nav_topups',
      label: 'Card top-ups',
      iconClass: 'pays-svg-icon ic-card-plus',
      children: [
        { label: 'Immediate', routerLink: 'card-topups/immediate' },
        { label: 'Recurring', routerLink: 'card-topups/recurring' },
        { label: 'Scheduled', routerLink: 'card-topups/scheduled' },
      ],
    },
    {
      id: 'nav_cards',
      label: 'Cards',
      iconClass: 'pays-svg-icon ic-card',
      children: [
        { label: 'Employee cards', routerLink: 'cards/employee-cards' },
        { label: 'Subscription cards', routerLink: 'cards/subscription-cards' },
      ],
    },
    {
      id: 'nav_fund',
      label: 'Fund account',
      iconClass: 'pays-svg-icon ic-card-arrow',
      routerLink: 'fund-account',
    },
    {
      id: 'nav_accounting',
      label: 'Accounting',
      iconClass: 'iconoir-calculator',
      children: [
        { label: 'Integrations', routerLink: 'accounting/integrations' },
        { label: 'Categories', routerLink: 'accounting/categories' },
        { label: 'Transactions', routerLink: 'accounting/transactions' },
      ],
    },
    {
      id: 'nav_plan',
      label: 'Your plan',
      iconClass: 'iconoir-badge-check',
      routerLink: 'your-plan',
    },
  ];
  protected readonly isMobile = this.responsiveService.isMobile;
  private readonly _navItemsSignal: WritableSignal<NavItem[]> = signal([]);
  public readonly navItems = computed(() => this._navItemsSignal());

  constructor() {
    this.pageTitle = this.store.select(AppSelectors.title);
    this.sideNavOpen$ = this.store.select(AppSelectors.sideNavOpen);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.sideNavOpen$.subscribe((sideNavOpen) => {
        this.sideNavOpen = sideNavOpen;
      }),
    );

    this.initializeNavItems();

    this.subscriptions.add(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          this.expandActiveMenus(event.urlAfterRedirects);
        }),
    );
  }

  private initializeNavItems(): void {
    // Wait for the subscription state to be loaded (hasAccounting will be undefined until loaded)
    this.subscriptions.add(
      this.hasAccounting$
        .pipe(
          filter((hasAccounting) => hasAccounting !== undefined), // Wait for state to load
          take(1), // Only take the first real value
        )
        .subscribe(() => {
          // Now that we know the state is loaded, read both values synchronously
          const hasSubCardAccess = this.store.selectSnapshot(SubscriptionSelectors.hasSubscriptionCards);
          const hasAccountingAccess = this.store.selectSnapshot(SubscriptionSelectors.hasAccounting);

          // Build the nav items
          const builtNavItems = this._baseNavItems.map((item) => {
            if (item.id === 'nav_cards') {
              return {
                ...item,
                // Mark the parent so the UI knows there is premium content inside
                needsPremiumAccess: true,
                hasPremiumAccess: hasSubCardAccess,
                children: item.children?.map((child) => {
                  if (child.label === 'Subscription cards') {
                    return {
                      ...child,
                      needsPremiumAccess: true,
                      hasPremiumAccess: hasSubCardAccess,
                    };
                  }
                  return { ...child };
                }),
              };
            }
            if (item.id === 'nav_accounting') {
              return {
                ...item,
                needsPremiumAccess: true,
                hasPremiumAccess: hasAccountingAccess,
                children: item.children?.map((child) => {
                  if (child.label === 'Integrations') {
                    return {
                      ...child,
                      needsPremiumAccess: true,
                      hasPremiumAccess: hasAccountingAccess,
                    };
                  }
                  return { ...child };
                }),
              };
            }
            return item;
          });

          // Update the signal with the built nav items
          this._navItemsSignal.set(builtNavItems);
          this.isLoading = false;

          // 1. Initialise all states to collapse (true)
          this.navItems().forEach((item) => {
            if (item.children) {
              this.collapseState[item.id] = true;
            }
          });

          // 2. Run logic on initial load
          this.expandActiveMenus(this.router.url);
        }),
    );
  }

  private expandActiveMenus(url: string): void {
    // Extract the path without query parameters to avoid false matches with query param values
    const urlPath = url.split('?')[0];

    this.helpCollapsed = !urlPath.includes('help-center');
    this.navItems().forEach((item) => {
      if (item.children) {
        // Check if the URL path (without query params) matches any child route
        const hasActiveChild = item.children.some((child) => {
          // Match the route path exactly - this prevents false matches.
          const routePath = `/${child.routerLink}`;
          return urlPath.includes(routePath) || urlPath === child.routerLink;
        });
        if (hasActiveChild) {
          this.collapseState[item.id] = false;
        }
      }
    });
  }

  onSideNavToggleClicked(): void {
    this.store.dispatch(new ToggleSideNav());
  }

  toggleCollapse(id: string): void {
    if (Object.hasOwn(this.collapseState, id)) {
      this.collapseState[id] = !this.collapseState[id];
    }
  }

  onOpenTutorial() {
    this.onCloseSideNav();
    const modalRef = this.modalService.open(SetUpTutorialModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.noApiRequired = true;
  }

  onCloseSideNav(): void {
    if (this.isMobile()) {
      this.store.dispatch(new CollapseSideNav());
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
